import {
    mdiAccount,
    mdiDotsVertical,
    mdiLightbulbOutline,
    mdiPercent,
} from '@mdi/js';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MuiTextField from '@mui/material/TextField';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';

import { Icon } from 'common/components/Icon';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { IconButton } from 'common/components/IconButton';
import { InputAdornment } from 'common/components/InputAdornment';
import {
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { ISkillListItemFragment } from 'graphql/types';

interface IProps extends React.ComponentProps<typeof ListItem> {
    fieldName: string;
    skill: ISkillListItemFragment;
    onDelete?(skill: ISkillListItemFragment): void;
}

export const GroupSkillFieldListItem = ({
    fieldName,
    skill,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [minAbsoluteField, minAbsoluteMeta] = useField(
        `${fieldName}.minAbsolute`
    );
    const [minPercentField, minPercentMeta] = useField(
        `${fieldName}.minPercent`
    );
    const { id, name } = skill || {};

    return (
        <ListItem
            button
            disableHover
            key={id}
            px={0}
            py={1}
            sx={{ flexWrap: 'wrap' }}
            {...other}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    color: 'primary.contrastText',
                    display: 'flex',
                    height: 40,
                    justifyContent: 'center',
                    mr: 2,
                    minWidth: 40,
                    width: 40,
                }}
            >
                <Icon path={mdiLightbulbOutline} size="2rem" />
            </Box>

            <ListItemText primary={name} sx={{ flex: 1, minWidth: 0 }} />

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    mt: { xs: 1.5, sm: 0 },
                    pl: { xs: 0, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                }}
            >
                <MuiTextField
                    {...minAbsoluteField}
                    error={!!minAbsoluteMeta.touched && !!minAbsoluteMeta.error}
                    helperText={
                        minAbsoluteMeta.touched && minAbsoluteMeta.error
                    }
                    label={translate('groupForm.minAbsolute')}
                    margin="none"
                    size="small"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    sx={{
                                        bgcolor: 'action.selected',
                                        borderRadius: '0 4px 4px 0',
                                        height: '100%',
                                        maxHeight: 'none',
                                        position: 'absolute',
                                        px: 1,
                                        right: 0,
                                        top: 0,
                                    }}
                                >
                                    <Icon path={mdiAccount} size="1.6rem" />
                                </InputAdornment>
                            ),
                            sx: { pr: 5 },
                        },
                    }}
                    sx={{ mr: 1, width: 88 }}
                    variant="outlined"
                />

                <MuiTextField
                    {...minPercentField}
                    error={!!minPercentMeta.touched && !!minPercentMeta.error}
                    helperText={minPercentMeta.touched && minPercentMeta.error}
                    label={translate('groupForm.minPercent')}
                    margin="none"
                    size="small"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    sx={{
                                        bgcolor: 'action.selected',
                                        borderRadius: '0 4px 4px 0',
                                        height: '100%',
                                        maxHeight: 'none',
                                        position: 'absolute',
                                        px: 1,
                                        right: 0,
                                        top: 0,
                                    }}
                                >
                                    <Icon path={mdiPercent} size="1.6rem" />
                                </InputAdornment>
                            ),
                            sx: { pr: 5 },
                        },
                    }}
                    sx={{ mr: 1, width: 88 }}
                    variant="outlined"
                />

                <ListItemSecondaryAction>
                    <DropdownMenu
                        anchor={
                            <IconButton
                                color="inherit"
                                iconPath={mdiDotsVertical}
                                iconSize="2.4rem"
                            />
                        }
                    >
                        <MenuItem onClick={() => onDelete?.(skill)}>
                            {translate('delete')}
                        </MenuItem>
                    </DropdownMenu>
                </ListItemSecondaryAction>
            </Box>
        </ListItem>
    );
};
